import { parseAgentSessionKey } from "../../../../src/routing/session-key.js";
import { buildAllDelegationMessage, detectMultiAgentIntent } from "./multi-agent-hint.ts";

export type FleetChatSendOptions = {
  orchestrateWithAll: boolean;
  createNewProject: boolean;
  selfImprovement: boolean;
};

export function hasSelfImprovementTarget(selfImprovement: boolean): boolean {
  return selfImprovement;
}

export function hasProjectSendTarget(createNewProject: boolean, projectId: string | null): boolean {
  return createNewProject || Boolean(projectId?.trim());
}

export function applyFleetSendOptionsToMessage(draft: string, opts: FleetChatSendOptions): string {
  const trimmed = draft.trim();
  if (!trimmed) {
    return trimmed;
  }
  if (opts.selfImprovement) {
    return trimmed;
  }
  if (opts.orchestrateWithAll && !/^@\S+/s.test(trimmed) && !/^\/best-of-n\b/i.test(trimmed)) {
    return buildAllDelegationMessage(trimmed);
  }
  return trimmed;
}

/** Human-readable project name from the user's prompt. */
export function deriveProjectNameFromMessage(text: string): string {
  let name = text
    .trim()
    .replace(/^(please\s+)?(create|build|make|design|implement|improve)\s+(a\s+)?/i, "")
    .replace(/\b(multi[- ]?agent\s+)?system\s+for\s+/i, "")
    .replace(/\bfor\s+improving\s+the\s+/i, "")
    .replace(/\bimproving\s+the\s+/i, "")
    .trim();
  if (!name) {
    name = "New project";
  }
  name = name.replace(/\s+/g, " ").slice(0, 56).trim();
  return name.length > 0 ? name : "New project";
}

/**
 * Random default name for auto-created projects when no project is selected.
 * Uses a short uppercase base36 suffix so users can quickly rename afterwards.
 */
export function generateAutoProjectName(random: () => number = Math.random): string {
  const value = Math.floor(random() * 36 ** 4);
  const suffix = value.toString(36).toUpperCase().padStart(4, "0");
  return `Project-${suffix}`;
}

/** @deprecated Use deriveProjectNameFromMessage */
export const deriveFleetProjectNameFromMessage = deriveProjectNameFromMessage;

export function isAgentMainSessionKey(sessionKey: string): boolean {
  const parsed = parseAgentSessionKey(sessionKey.trim());
  return parsed?.rest === "main";
}

export function isHeartbeatAckOnly(text: string): boolean {
  const t = text.trim();
  return /^HEARTBEAT_OK\.?$/i.test(t) || /^HEARTBEAT_OK\s*$/i.test(t);
}

export function messageTextContent(message: unknown): string {
  const m = message as Record<string, unknown>;
  if (typeof m.content === "string") {
    return m.content;
  }
  if (Array.isArray(m.content)) {
    return m.content
      .map((item) => {
        const x = item as Record<string, unknown>;
        return typeof x.text === "string" ? x.text : "";
      })
      .join("\n")
      .trim();
  }
  if (typeof m.text === "string") {
    return m.text;
  }
  return "";
}

export function shouldReplaceHeartbeatAckWithHint(params: {
  userText: string;
  assistantText: string;
}): boolean {
  if (!isHeartbeatAckOnly(params.assistantText)) {
    return false;
  }
  const user = params.userText.trim();
  if (user.length < 12) {
    return false;
  }
  return (
    detectMultiAgentIntent(user) || /\b(improve|implement|refactor|fleet control)\b/i.test(user)
  );
}
